// src/context/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config';

export const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

function parseJwtPayload(token) {
  try {
    const base64Payload = token.split('.')[1];
    if (!base64Payload) return null;
    const jsonPayload = atob(base64Payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

function isTokenValid(token) {
  if (!token) return false;
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return false;
  return payload.exp * 1000 > Date.now();
}

async function fetchUserProfile(token) {
  try {
    const res = await fetch(`${API_URL}/api/profile/`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) return await res.json();
  } catch {}
  return null;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const initAuth = async () => {
      if (isTokenValid(token)) {
        const profile = await fetchUserProfile(token);
        setUser({ authenticated: true, first_name: profile?.first_name || null });
      } else {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password, turnstileToken = null) => {
    const response = await fetch(`${API_URL}/api/token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: email,  // simplejwt expects 'username' field; we pass email as the value
        password,
        ...(turnstileToken ? { cf_turnstile_response: turnstileToken } : {}),
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err = new Error(errorData.detail || 'Invalid credentials');
      err.code = errorData.code || null;
      err.email = email;
      throw err;
    }

    const data = await response.json();
    if (!isTokenValid(data.access)) {
      throw new Error('Received invalid token');
    }
    localStorage.setItem('access_token', data.access);
    localStorage.setItem('refresh_token', data.refresh);
    setToken(data.access);
    const profile = await fetchUserProfile(data.access);
    setUser({ authenticated: true, email, first_name: profile?.first_name || null });
    navigate('/');
  };

  const signup = async (email, password, turnstileToken = null, honeypot = '', elapsedMs = null) => {
    const response = await fetch(`${API_URL}/api/register/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        ...(turnstileToken ? { cf_turnstile_response: turnstileToken } : {}),
        honeypot,
        ...(elapsedMs !== null ? { elapsed_ms: elapsedMs } : {}),
      })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail || (errorData.email ? errorData.email[0] : 'Signup failed')
      );
    }
    const data = await response.json();
    return { email: data.email };
  };

  const verifyEmail = async (email, code) => {
    const response = await fetch(`${API_URL}/api/verify-email/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err = new Error(errorData.detail || 'Verification failed');
      err.code = errorData.code || null;
      throw err;
    }
    const data = await response.json();
    if (!isTokenValid(data.access)) {
      throw new Error('Received invalid token');
    }
    localStorage.setItem('access_token', data.access);
    localStorage.setItem('refresh_token', data.refresh);
    setToken(data.access);
    const profile = await fetchUserProfile(data.access);
    setUser({ authenticated: true, email, first_name: profile?.first_name || null });
    navigate('/');
  };

  const resendVerification = async (email) => {
    const response = await fetch(`${API_URL}/api/resend-verification/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to resend code');
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    navigate('/login');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, signup, verifyEmail, resendVerification, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
